import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";

const PDF_UNICODE_FONT_NAME = "NotoSansSC";
const PDF_UNICODE_FONT_FILE = "NotoSansSC-Regular.ttf";
let pdfUnicodeFontBase64Promise;

const CHAR_WIDTH_PX = 7.2;
const LINE_HEIGHT_PX = 18;
const MIN_ROW_HEIGHT_PX = 36;
const MIN_WRAPPED_ROW_HEIGHT_PX = 48;
const TITLE_ROW_HEIGHT_PX = 44;
const HEADER_ROW_HEIGHT_PX = 34;

function arrayBufferToBase64(buffer) {
  const bytes = new Uint8Array(buffer);
  const chunkSize = 0x8000;
  let binary = "";
  for (let offset = 0; offset < bytes.length; offset += chunkSize) {
    binary += String.fromCharCode(...bytes.subarray(offset, offset + chunkSize));
  }
  return window.btoa(binary);
}

async function loadPdfUnicodeFont() {
  if (!pdfUnicodeFontBase64Promise) {
    pdfUnicodeFontBase64Promise = fetch(`/upload/${PDF_UNICODE_FONT_FILE}`)
      .then(async (response) => {
        if (!response.ok) {
          throw new Error(`Unable to load PDF font (${response.status}).`);
        }
        const contentType = `${response.headers.get("content-type") ?? ""}`.toLowerCase();
        if (contentType.includes("text/html")) {
          throw new Error("PDF Unicode font is missing from the application build.");
        }
        return arrayBufferToBase64(await response.arrayBuffer());
      })
      .catch((error) => {
        pdfUnicodeFontBase64Promise = undefined;
        throw error;
      });
  }
  return pdfUnicodeFontBase64Promise;
}

export async function registerPdfUnicodeFont(doc) {
  const fontBase64 = await loadPdfUnicodeFont();
  doc.addFileToVFS(PDF_UNICODE_FONT_FILE, fontBase64);
  doc.addFont(PDF_UNICODE_FONT_FILE, PDF_UNICODE_FONT_NAME, "normal", "Identity-H");
  doc.addFont(PDF_UNICODE_FONT_FILE, PDF_UNICODE_FONT_NAME, "bold", "Identity-H");
}

export function textWidthUnits(text) {
  let units = 0;
  for (const char of `${text ?? ""}`) {
    units += /[\u1100-\u115F\u2E80-\uA4CF\uAC00-\uD7A3\uF900-\uFAFF\uFE30-\uFE4F\uFF00-\uFF60\uFFE0-\uFFE6]/.test(
      char
    )
      ? 2
      : 1;
  }
  return units;
}

function sliceByWidthUnits(text, maxUnits) {
  const chunks = [];
  let current = "";
  let units = 0;
  for (const char of `${text ?? ""}`) {
    const charUnits = textWidthUnits(char);
    if (units + charUnits > maxUnits && current) {
      chunks.push(current);
      current = "";
      units = 0;
    }
    current += char;
    units += charUnits;
  }
  if (current) chunks.push(current);
  return chunks.length > 0 ? chunks : [""];
}

export function wrapTextForColumn(text, widthPx) {
  const raw = `${text ?? ""}`.replace(/\r\n/g, "\n").trim();
  if (!raw) return { text: "", lines: 1 };

  const unitsPerLine = Math.max(6, Math.floor((widthPx - 20) / CHAR_WIDTH_PX));
  const wrappedLines = [];

  raw.split("\n").forEach((paragraph) => {
    const words = `${paragraph}`.split(/\s+/).filter(Boolean);
    if (words.length === 0) {
      wrappedLines.push("");
      return;
    }

    let current = "";
    words.forEach((word) => {
      const candidate = current ? `${current} ${word}` : word;
      if (textWidthUnits(candidate) <= unitsPerLine) {
        current = candidate;
        return;
      }

      if (current) wrappedLines.push(current);

      if (textWidthUnits(word) <= unitsPerLine) {
        current = word;
        return;
      }

      const chunks = sliceByWidthUnits(word, unitsPerLine);
      wrappedLines.push(...chunks.slice(0, -1));
      current = chunks[chunks.length - 1];
    });

    if (current) wrappedLines.push(current);
  });

  return { text: wrappedLines.join("\n"), lines: Math.max(1, wrappedLines.length) };
}

export function ensureExtension(filename, ext) {
  const base = `${filename || "download"}`.replace(/\.[^.]+$/, "");
  return `${base}${ext}`;
}

export function triggerBlobDownload(buffer, mimeType, filename) {
  const blob = new Blob([buffer], { type: mimeType });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.setAttribute("download", filename);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
}

/**
 * Generic FortuneSheet workbook for medical form tables (title + header + data).
 * @param {{
 *   title: string,
 *   headers: string[],
 *   rows: string[][],
 *   columnWidthsPx: number[],
 *   name?: string,
 * }} model
 */
export function buildMedicalFortuneSheetData(model) {
  const columnCount = model.headers.length;
  const celldata = [];
  const merge = {};
  const rowlen = {};
  const customHeight = {};
  const columnlen = {};
  const dataVerification = {};

  model.columnWidthsPx.forEach((width, index) => {
    columnlen[`${index}`] = width;
  });

  const titleText = model.title;
  celldata.push({
    r: 0,
    c: 0,
    v: {
      v: titleText,
      m: titleText,
      ct: { fa: "@", t: "s" },
      ff: "Times New Roman",
      fs: 16,
      bl: 1,
      ht: 0,
      vt: 0,
      tb: "2",
      bg: "#FFFFFF",
      mc: { r: 0, c: 0, rs: 1, cs: columnCount },
    },
  });
  // Keep merged title cells untinted (Fortune may paint non-master cells).
  for (let c = 1; c < columnCount; c += 1) {
    celldata.push({
      r: 0,
      c,
      v: {
        ct: { fa: "@", t: "s" },
        bg: "#FFFFFF",
        mc: { r: 0, c: 0, rs: 1, cs: columnCount },
      },
    });
  }
  merge["0_0"] = { r: 0, c: 0, rs: 1, cs: columnCount };
  rowlen["0"] = TITLE_ROW_HEIGHT_PX;
  customHeight["0"] = 1;

  model.headers.forEach((header, c) => {
    const text = `${header ?? ""}`;
    celldata.push({
      r: 1,
      c,
      v: {
        v: text,
        m: text,
        ct: { fa: "@", t: "s" },
        ff: "Arial",
        fs: 12,
        bl: 1,
        ht: 0,
        vt: 0,
        tb: "2",
        // Match customer-info table header (#0066B1 / white).
        bg: "#0066B1",
        fc: "#FFFFFF",
      },
    });
  });
  rowlen["1"] = model.headerRowHeightPx || HEADER_ROW_HEIGHT_PX;
  customHeight["1"] = 1;

  const noFill = model.noFillColor || "#DD7E6B";
  model.rows.forEach((row, rowIndex) => {
    const r = rowIndex + 2;
    let maxLines = 1;
    row.forEach((value, c) => {
      const width = model.columnWidthsPx[c] || 100;
      const wrapped = wrapTextForColumn(value, width);
      if (wrapped.lines > maxLines) maxLines = wrapped.lines;
      const text = wrapped.text || `${value ?? ""}`;
      const ht =
        model.halignByColumn?.[c] != null
          ? model.halignByColumn[c]
          : c === 0
            ? 0
            : 1;
      const isNo = `${text}`.trim().toLowerCase() === "no";
      const extraBg =
        typeof model.cellBackground === "function"
          ? model.cellBackground(rowIndex, c, text)
          : null;
      celldata.push({
        r,
        c,
        v: {
          v: text,
          m: text,
          ct: { fa: "@", t: "s" },
          ff: "Arial",
          fs: 12,
          ht,
          vt: 0,
          tb: "2",
          ...(extraBg
            ? { bg: extraBg }
            : model.highlightNo && isNo
              ? { bg: noFill, fc: model.noTextColor || "#000000" }
              : {}),
        },
      });
    });
    rowlen[`${r}`] = Math.max(
      MIN_ROW_HEIGHT_PX,
      maxLines > 1 ? MIN_WRAPPED_ROW_HEIGHT_PX : MIN_ROW_HEIGHT_PX,
      maxLines * LINE_HEIGHT_PX
    );
    customHeight[`${r}`] = 1;

    if (model.dataVerificationByColumn) {
      Object.entries(model.dataVerificationByColumn).forEach(([colKey, verification]) => {
        dataVerification[`${r}_${colKey}`] = { ...verification };
      });
    }
  });

  return [
    {
      name: model.name || "Sheet1",
      celldata,
      dataVerification,
      row: Math.max(40, model.rows.length + 4),
      column: columnCount,
      config: {
        merge,
        rowlen,
        customHeight,
        columnlen,
        // The title is rendered above the Medical Form frame; keep the
        // worksheet row for data/export indexing, but hide it on screen.
        rowhidden: { "0": 0 },
      },
      // Freeze through header row (row 1). Hidden title row 0 stays above it.
      frozen: {
        type: "row",
        range: { row_focus: 1, column_focus: 0 },
      },
      scrollLeft: 0,
      scrollTop: 0,
      // Avoid default A1/title-row selection highlight on mount.
      luckysheet_select_save: [],
    },
  ];
}

function fitColumnStylesToPage(
  columnStyles,
  tableWidth,
  { fill = false, fillFromIndex = 0 } = {}
) {
  const keys = Object.keys(columnStyles || {});
  if (keys.length === 0 || !Number.isFinite(tableWidth) || tableWidth <= 0) {
    return columnStyles;
  }
  const widths = keys.map((key) => Number(columnStyles[key]?.cellWidth) || 0);
  const total = widths.reduce((sum, width) => sum + width, 0);
  if (total <= 0) return columnStyles;
  const needsShrink = total > tableWidth;
  const needsFill = fill && Math.abs(total - tableWidth) >= 0.5;
  if (!needsShrink && !needsFill) return columnStyles;

  const stretchFrom = fill && !needsShrink ? Math.max(0, Number(fillFromIndex) || 0) : 0;
  const keepTotal = widths.slice(0, stretchFrom).reduce((sum, width) => sum + width, 0);
  const stretchTotal = widths.slice(stretchFrom).reduce((sum, width) => sum + width, 0);
  const available = tableWidth - keepTotal;
  if (!(stretchTotal > 0) || !(available > 0)) {
    const scale = tableWidth / total;
    const fitted = {};
    keys.forEach((key, index) => {
      fitted[key] = { ...columnStyles[key], cellWidth: widths[index] * scale };
    });
    return fitted;
  }
  const scale = available / stretchTotal;
  const fitted = {};
  keys.forEach((key, index) => {
    fitted[key] = {
      ...columnStyles[key],
      cellWidth: index < stretchFrom ? widths[index] : widths[index] * scale,
    };
  });
  return fitted;
}

export async function downloadMedicalTablePdf({
  title,
  headers,
  rows,
  filename,
  columnStyles = {},
  pageOptions = {},
}) {
  const orientation = pageOptions.orientation || "landscape";
  const marginX = pageOptions.marginX ?? 28;
  const marginY = pageOptions.marginY ?? 28;
  const fontSize = pageOptions.fontSize ?? 8;
  const headFontSize = pageOptions.headFontSize ?? fontSize;
  const titleFontSize = pageOptions.titleFontSize ?? 14;
  const rowsPerPage = pageOptions.rowsPerPage;
  const cellPadding = pageOptions.cellPadding ?? 3;
  const requestedHeaderHeight = pageOptions.headerHeight;
  const lineColor = pageOptions.lineColor || [0, 0, 0];
  const lineWidth = pageOptions.lineWidth ?? 0.5;

  const doc = new jsPDF({ orientation, unit: "pt", format: "letter" });
  await registerPdfUnicodeFont(doc);
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const bodyRows = rows.map((row) => row.map((cell) => `${cell ?? ""}`));
  const chunks =
    rowsPerPage && rowsPerPage > 0
      ? Array.from({ length: Math.max(1, Math.ceil(bodyRows.length / rowsPerPage)) }, (_, i) =>
          bodyRows.slice(i * rowsPerPage, (i + 1) * rowsPerPage)
        )
      : [bodyRows];

  chunks.forEach((chunk, pageIndex) => {
    if (pageIndex > 0) doc.addPage();

    const showTitle = Boolean(title) && (!pageOptions.titleOnFirstPageOnly || pageIndex === 0);
    let startY = marginY;
    if (showTitle) {
      doc.setFont(PDF_UNICODE_FONT_NAME, "bold");
      doc.setFontSize(titleFontSize);
      doc.setTextColor(0, 0, 0);
      const titleLines = `${title || ""}`
        .split(/\n/)
        .flatMap((line) => doc.splitTextToSize(`${line}`.trim() || " ", pageWidth - marginX * 2));
      const titleLineHeight = titleFontSize + 4;
      const titleY = marginY + titleFontSize;
      doc.text(titleLines, pageWidth / 2, titleY, { align: "center" });
      startY = titleY + titleLines.length * titleLineHeight - titleFontSize + 8;
    }
    const footerOnLastPageOnly = Boolean(pageOptions.footerOnLastPageOnly);
    const footerHeight = pageOptions.footerHeight ?? 0;
    const reservedFooter = footerOnLastPageOnly ? 0 : footerHeight;
    const tableWidth = pageWidth - marginX * 2;
    const availableHeight = pageHeight - startY - marginY - reservedFooter;
    const headerHeight = requestedHeaderHeight ?? Math.max(22, headFontSize + 12);
    const dataRowCount = Math.max(chunk.length, 1);
    const minCellHeight = rowsPerPage
      ? Math.max(18, (availableHeight - headerHeight - 4) / Math.max(rowsPerPage, dataRowCount))
      : undefined;

    doc.setFont(PDF_UNICODE_FONT_NAME, "normal");
    doc.setFontSize(fontSize);

    const tableStyles = {
      font: PDF_UNICODE_FONT_NAME,
      fontStyle: "normal",
      fontSize,
      cellPadding,
      valign: "middle",
      overflow: "linebreak",
      lineColor,
      lineWidth,
      textColor: [0, 0, 0],
      halign: "center",
    };
    if (Number.isFinite(minCellHeight)) {
      tableStyles.minCellHeight = minCellHeight;
    } else {
      tableStyles.minCellHeight = Math.max(16, fontSize + 6);
    }

    const headStyles = {
      font: PDF_UNICODE_FONT_NAME,
      fillColor: pageOptions.headFillColor || [255, 255, 255],
      textColor: [0, 0, 0],
      fontStyle: "bold",
      fontSize: headFontSize,
      halign: "center",
      valign: "middle",
      lineColor,
      lineWidth,
    };
    if (Number.isFinite(headerHeight)) {
      headStyles.minCellHeight = headerHeight;
    }

    const tableOptions = {
      startY,
      head: [headers],
      body: chunk,
      tableWidth,
      styles: tableStyles,
      headStyles,
      columnStyles: fitColumnStylesToPage(columnStyles, tableWidth, {
        fill: Boolean(pageOptions.fillTableWidth),
        fillFromIndex: pageOptions.fillFromIndex,
      }),
      margin: { left: marginX, right: marginX, bottom: marginY + reservedFooter },
      rowPageBreak: "avoid",
    };
    if (typeof pageOptions.drawFooter === "function" && !footerOnLastPageOnly) {
      tableOptions.didDrawPage = () => {
        pageOptions.drawFooter(doc, {
          pageWidth,
          pageHeight,
          marginX,
          marginY,
          footerHeight,
        });
      };
    }
    const parseCellHooks = [];
    if (pageOptions.highlightNo) {
      parseCellHooks.push((hookData) => {
        if (hookData.section !== "body") return;
        const text = `${hookData.cell.raw ?? ""}`.trim().toLowerCase();
        if (text === "no") {
          hookData.cell.styles.fillColor = pageOptions.noFillRgb || [221, 126, 107];
          hookData.cell.styles.textColor = pageOptions.noTextRgb || [0, 0, 0];
        }
      });
    }
    if (typeof pageOptions.didParseCell === "function") {
      parseCellHooks.push(pageOptions.didParseCell);
    }
    if (parseCellHooks.length > 0) {
      tableOptions.didParseCell = (hookData) => {
        parseCellHooks.forEach((fn) => fn(hookData));
      };
    }

    autoTable(doc, tableOptions);
  });

  if (typeof pageOptions.drawFooter === "function" && pageOptions.footerOnLastPageOnly) {
    const footerHeight = pageOptions.footerHeight ?? 0;
    doc.setPage(doc.internal.getNumberOfPages());
    const finalY = doc.lastAutoTable?.finalY ?? marginY;
    let footerTop = finalY + 10;
    if (footerTop + footerHeight > pageHeight - marginY) {
      doc.addPage();
      footerTop = marginY;
    }
    pageOptions.drawFooter(doc, {
      pageWidth,
      pageHeight,
      marginX,
      marginY,
      footerHeight,
      footerTop,
    });
  }

  doc.save(ensureExtension(filename, ".pdf"));
}

export { PDF_UNICODE_FONT_NAME };
